// src/pages/RelatorioVolumeProducao.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  TextField, Button, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, TablePagination
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  Medication as CapsuleIcon,
  Factory as FactoryIcon,
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

const RelatorioVolumeProducao = () => {
  const { api } = useAuth();
  const location = useLocation();
  
  // Suprimir erro do proxy (extensões do browser)
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0]?.toString().includes('disconnected port object')) {
        return; // Ignorar erro do proxy
      }
      originalError.apply(console, args);
    };
    
    return () => {
      console.error = originalError;
    };
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dados, setDados] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    setor: 'Encapsulação', // Sempre focado em Encapsulação
    funcionario: '',
    numeroOrdemProducao: ''
  });
  const [setores, setSetores] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [dadosDetalhados, setDadosDetalhados] = useState([]);
  const [mostrarDetalhes, setMostrarDetalhes] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [temProximaPagina, setTemProximaPagina] = useState(false);
  const [distribuicao, setDistribuicao] = useState([]);
  const [resumoQuantidades, setResumoQuantidades] = useState({});
  const [dadosFuncionarios, setDadosFuncionarios] = useState([]);

  // Definir período padrão (mês atual) e detectar parâmetros da URL
  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    
    // Obter parâmetros da URL
    const urlParams = new URLSearchParams(location.search);
    const setorUrl = urlParams.get('setor');

    setFiltros(prev => ({
      ...prev,
      dataInicio: primeiroDia.toISOString().split('T')[0],
      dataFim: ultimoDia.toISOString().split('T')[0],
      setor: setorUrl || ''
    }));

    // Se veio com setor específico, mostrar detalhes
    if (setorUrl) {
      setMostrarDetalhes(true);
    }

  }, [location.search]);
  
  // Define loaders and effects below to avoid no-use-before-define
  
  const carregarDistribuicao = useCallback(async () => {
    try {
      const parametros = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim,
        setor: filtros.setor
      };

      if (filtros.funcionario) {
        parametros.funcionario = filtros.funcionario;
      }

      console.log('📊 Carregando distribuição por quantidade...');
      const response = await api.relatorioVolumeProducaoDistribuicao(parametros);
      
      if (response && response.success) {
        console.log('✅ Distribuição carregada:', response.dados);
        setDistribuicao(response.dados || []);
      } else {
        console.warn('⚠️ Erro ao carregar distribuição:', response?.message);
        setDistribuicao([]);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar distribuição:', error);
      setDistribuicao([]);
    }
  }, [api, filtros]);

  const carregarDadosDetalhados = useCallback(async () => {
    const parametros = {
      dataInicio: filtros.dataInicio,
      dataFim: filtros.dataFim,
      setor: 'Encapsulação', // Sempre Encapsulação
      limite: 50000
    };

    if (filtros.funcionario) {
      parametros.funcionario = filtros.funcionario;
    }

    console.log('� Carregando dados detalhados para', filtros.setor);
    console.log('📅 Período:', parametros.dataInicio, 'até', parametros.dataFim);
    console.log('⚙️ Parâmetros completos:', parametros);

    console.log('🌐 Chamando API relatorioVolumeProducao...');
    const response = await api.relatorioVolumeProducao(parametros);
    console.log('📡 Resposta da API:', response);
    
    if (response && response.success) {
      console.log('✅ API retornou sucesso');
      const leituras = response.dados || [];
      console.log(`📋 Total de leituras recebidas: ${leituras.length}`);
      console.log('🔍 Amostra dos dados:', leituras.slice(0, 2));
      
      // Os dados já vêm filtrados com quantidade do backend
      setDadosDetalhados(leituras);
      
      // Usar estatísticas do backend
      const estatisticas = response.meta?.estatisticas || {};
      setEstatisticas(estatisticas);
      
      console.log(`✅ Carregadas ${leituras.length} requisições com quantidade`);
      console.log('📊 Total de cápsulas:', estatisticas.total_capsulas);
      console.log('🏭 Distribuição por quantidade:', estatisticas.requisicoes_por_quantidade);
      
      // Carregar distribuição para o gráfico
      await carregarDistribuicao();
    } else {
      console.error('❌ Erro na resposta da API');
      console.error('📄 Resposta completa:', response);
      const mensagemErro = response?.message || response?.error || 'Erro ao carregar dados detalhados';
      console.error('💥 Mensagem de erro:', mensagemErro);
      setError(mensagemErro);
    }
  }, [api, filtros, carregarDistribuicao]);

  const carregarSetores = useCallback(async () => {
    try {
      const response = await api.listarSetores();
      if (response.success) {
        // Filtrar apenas os setores específicos para análise de volume de produção
        const setoresPermitidos = ['Encapsulação', 'Sachê', 'Xarope', 'Dermato', 'Ordem de Produção', 'Ortofarma'];
        const setoresFiltrados = (response.dados || []).filter(setor => 
          setoresPermitidos.includes(setor.nome)
        );
        setSetores(setoresFiltrados);
      }
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  }, [api]);

  const carregarFuncionarios = useCallback(async () => {
    try {
      console.log('🔄 Carregando todos os funcionários...');
      const response = await api.listarFuncionarios();
      console.log('📡 Resposta API funcionários:', response);
      if (response.success) {
        const funcionariosData = response.dados || [];
        console.log('👥 Funcionários carregados:', funcionariosData.length);
        setFuncionarios(funcionariosData);
      } else {
        console.error('❌ Erro na API de funcionários:', response.message);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar funcionários:', error);
    }
  }, [api]);

  const carregarDados = useCallback(async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período (data início e fim)');
      return;
    }

        console.log('🔍 Filtros atuais:', filtros);
        console.log('🔍 funcionario:', filtros.funcionario, 'tipo:', typeof filtros.funcionario);

    // Prevenir múltiplas chamadas simultâneas
    if (loading) {
      console.log('⚠️ Carregamento já em andamento, ignorando nova chamada');
      return;
    }

    try {
      setLoading(true);
      setError('');
      console.log('📊 Iniciando carregamento de dados...');

        // Verificar se deve carregar dados detalhados ou resumidos
        const deveCarregarDetalhado = mostrarDetalhes; // Sempre Encapsulação, então só depende de mostrarDetalhes
      
      if (deveCarregarDetalhado) {
        // Carregar dados detalhados para o setor específico
        await carregarDadosDetalhados();
      } else {
            // Carregar dados resumidos usando o novo endpoint de volume de produção
            const parametros = {
              dataInicio: filtros.dataInicio,
              dataFim: filtros.dataFim,
              setor: 'Encapsulação', // Sempre Encapsulação
              tipoQuantidade: '1', // Sempre tipo 1 (Cápsulas)
              limite: 50000, // Limite muito alto para garantir todos os dados
              pagina: page + 1
            };

        if (filtros.funcionario && filtros.funcionario !== '') {
          parametros.funcionario = filtros.funcionario;
        }

        if (filtros.numeroOrdemProducao) {
          parametros.ordemProducao = filtros.numeroOrdemProducao;
        }

        console.log('📦 Parâmetros sendo enviados:', parametros);
        const response = await api.relatorioVolumeProducao(parametros);
        console.log('📡 Resposta da API:', response);
        
            if (response.success) {
              const dados = response.dados || [];
              console.log('📊 Dados recebidos:', dados.length, 'registros');
              
              // Log dos primeiros dados para debug
              if (dados.length > 0) {
                console.log('🔍 Primeiro registro da API:', dados[0]);
                console.log('🔍 Campos disponíveis:', Object.keys(dados[0]));
                
                // Verificar se há registros de 30 cápsulas
                const registros30 = dados.filter(d => d.quantidade === 30);
                console.log('🔍 Registros de 30 cápsulas encontrados:', registros30.length);
                if (registros30.length > 0) {
                  console.log('🔍 Primeiro registro de 30 cápsulas:', registros30[0]);
                }
                
                // Verificar quantidades únicas
                const quantidadesUnicas = [...new Set(dados.map(d => d.quantidade))].sort((a, b) => a - b);
                console.log('🔍 Quantidades únicas encontradas:', quantidadesUnicas);
              }
              
              // Processar dados para o novo formato
              processarDadosPorQuantidade(dados);
              
              setDados(dados);
              setEstatisticas(response.meta?.estatisticas || null);
              
              // Atualizar informações de paginação
              if (response.meta?.estatisticas?.paginacao) {
                const paginacao = response.meta.estatisticas.paginacao;
                setTotalRegistros(paginacao.total_registros);
                setTemProximaPagina(paginacao.tem_proxima_pagina);
              }
            } else {
              setError(response.message || 'Erro ao carregar dados');
            }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar relatório:', error);
      setError(`Erro ao carregar dados: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
      console.log('✅ Carregamento finalizado');
    }
  }, [filtros, mostrarDetalhes, api, carregarDadosDetalhados, loading]);

  // Recarregar dados quando página ou limite mudarem
  useEffect(() => {
    if (filtros.dataInicio && filtros.dataFim) {
      carregarDados();
    }
  }, [page, rowsPerPage]);

  // Funções de paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Função para imprimir relatório
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Relatório de Volume de Produção</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .summary { display: flex; justify-content: space-around; margin: 20px 0; }
            .summary-card { text-align: center; padding: 15px; border: 1px solid #ddd; border-radius: 8px; margin: 0 10px; }
            .summary-card h3 { margin: 0; color: #1976d2; }
            .summary-card p { margin: 5px 0; font-size: 24px; font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .period { margin-bottom: 20px; font-size: 14px; color: #666; }
            @media print {
              body { margin: 0; }
              .summary { page-break-inside: avoid; }
              table { page-break-inside: auto; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Relatório de Volume de Produção</h1>
            <p>Análise completa do volume de produção por setor</p>
            <div class="period">
              <strong>Período:</strong> ${filtros.dataInicio} a ${filtros.dataFim}
            </div>
          </div>
          
          <div class="summary">
            <div class="summary-card">
              <h3>30 Cápsulas</h3>
              <p style="color: #1976d2;">${resumoQuantidades[30] || 0}</p>
            </div>
            <div class="summary-card">
              <h3>60 Cápsulas</h3>
              <p style="color: #9c27b0;">${resumoQuantidades[60] || 0}</p>
            </div>
            <div class="summary-card">
              <h3>120 Cápsulas</h3>
              <p style="color: #4caf50;">${resumoQuantidades[120] || 0}</p>
            </div>
            <div class="summary-card">
              <h3>Acima de 120</h3>
              <p style="color: #ff9800;">${resumoQuantidades['acima120'] || 0}</p>
            </div>
          </div>
          
          <h2>Produção por Funcionário</h2>
          <table>
            <thead>
              <tr>
                <th>Funcionário</th>
                <th>Setor</th>
                <th>30 Cápsulas</th>
                <th>60 Cápsulas</th>
                <th>120 Cápsulas</th>
                <th>Acima de 120</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${dadosFuncionarios.map(func => `
                <tr>
                  <td>${escapeHtml(func.nome)}</td>
                  <td>${escapeHtml(func.setor)}</td>
                  <td>${func.quantidade30}</td>
                  <td>${func.quantidade60}</td>
                  <td>${func.quantidade120}</td>
                  <td>${func.quantidadeAcima}</td>
                  <td><strong>${func.total}</strong></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Função para exportar CSV
  const handleExportCSV = () => {
    const csvContent = [
      // Cabeçalho
      ['Funcionário', 'Setor', '30 Cápsulas', '60 Cápsulas', '120 Cápsulas', 'Acima de 120', 'Total'],
      // Dados
      ...dadosFuncionarios.map(func => [
        func.nome,
        func.setor,
        func.quantidade30,
        func.quantidade60,
        func.quantidade120,
        func.quantidadeAcima,
        func.total
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio_volume_producao_${filtros.dataInicio}_${filtros.dataFim}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Função para exportar PDF
  const handleExportPDF = () => {
    const doc = new jsPDF('landscape');
    
    // Título
    doc.setFontSize(20);
    doc.text('Relatório de Volume de Produção', 20, 20);
    
    // Subtítulo
    doc.setFontSize(12);
    doc.text('Análise completa do volume de produção por setor', 20, 30);
    
    // Período
    doc.setFontSize(10);
    doc.text(`Período: ${filtros.dataInicio} a ${filtros.dataFim}`, 20, 40);
    
    // Resumo geral
    doc.setFontSize(14);
    doc.text('Resumo Geral por Quantidade de Cápsulas', 20, 55);
    
    // Cards do resumo
    const cardWidth = 40;
    const cardHeight = 20;
    const startX = 20;
    const startY = 65;
    
    // 30 Cápsulas
    doc.setFillColor(25, 118, 210);
    doc.rect(startX, startY, cardWidth, cardHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text('30 Cápsulas', startX + 2, startY + 8);
    doc.setFontSize(16);
    doc.text(`${resumoQuantidades[30] || 0}`, startX + 2, startY + 16);
    
    // 60 Cápsulas
    doc.setFillColor(156, 39, 176);
    doc.rect(startX + 50, startY, cardWidth, cardHeight, 'F');
    doc.text('60 Cápsulas', startX + 52, startY + 8);
    doc.setFontSize(16);
    doc.text(`${resumoQuantidades[60] || 0}`, startX + 52, startY + 16);
    
    // 120 Cápsulas
    doc.setFillColor(76, 175, 80);
    doc.rect(startX + 100, startY, cardWidth, cardHeight, 'F');
    doc.text('120 Cápsulas', startX + 102, startY + 8);
    doc.setFontSize(16);
    doc.text(`${resumoQuantidades[120] || 0}`, startX + 102, startY + 16);
    
    // Acima de 120
    doc.setFillColor(255, 152, 0);
    doc.rect(startX + 150, startY, cardWidth, cardHeight, 'F');
    doc.text('Acima de 120', startX + 152, startY + 8);
    doc.setFontSize(16);
    doc.text(`${resumoQuantidades['acima120'] || 0}`, startX + 152, startY + 16);
    
    // Resetar cor do texto
    doc.setTextColor(0, 0, 0);
    
    // Tabela de funcionários
    doc.setFontSize(14);
    doc.text('Produção por Funcionário', 20, 100);
    
    const tableData = dadosFuncionarios.map(func => [
      func.nome,
      func.setor,
      func.quantidade30,
      func.quantidade60,
      func.quantidade120,
      func.quantidadeAcima,
      func.total
    ]);
    
    doc.autoTable({
      head: [['Funcionário', 'Setor', '30 Cápsulas', '60 Cápsulas', '120 Cápsulas', 'Acima de 120', 'Total']],
      body: tableData,
      startY: 110,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [245, 245, 245] }
    });
    
    // Salvar PDF
    doc.save(`relatorio_volume_producao_${filtros.dataInicio}_${filtros.dataFim}.pdf`);
  };

  // Função para processar dados por quantidade de cápsulas
  const processarDadosPorQuantidade = useCallback((dados) => {
    console.log('🔄 Processando dados por quantidade de cápsulas...');
    console.log('📊 Dados recebidos:', dados.length, 'registros');
    console.log('👥 Funcionários disponíveis:', funcionarios.length);
    console.log('🔍 Funcionário selecionado:', filtros.funcionario || 'Nenhum');
    
    // Log dos primeiros dados para debug
    if (dados.length > 0) {
      console.log('🔍 Primeiro registro:', dados[0]);
      console.log('🔍 Nomes de funcionários nos dados:', dados.slice(0, 5).map(d => d.funcionario_nome));
    }
    
    // Log dos funcionários carregados
    console.log('👥 Funcionários carregados:', funcionarios.map(f => f.nome));
    
    // Agrupar por quantidade de cápsulas para resumo geral (categorias específicas + Outros)
    const resumoPorQuantidade = {
      30: 0,
      60: 0,
      120: 0,
      'acima120': 0,
      'outros': 0  // Quantidades que não se encaixam nas categorias padrão
    };
    const dadosPorFuncionario = {};
    
    // Primeiro, inicializar funcionários com zeros
    // Se não há funcionário selecionado, inicializar apenas funcionários da Encapsulação
    // Se há funcionário selecionado, inicializar apenas esse funcionário
    const criarEntradaFuncionario = (nome, setor) => ({
      nome,
      setor,
      quantidade30: 0,
      quantidade60: 0,
      quantidade120: 0,
      quantidadeAcima: 0,
      quantidadeOutros: 0,  // Quantidades fora das categorias padrão
      total: 0
    });

    if (!filtros.funcionario || filtros.funcionario === '') {
      // Sem funcionário selecionado: inicializar todos da Encapsulação
      console.log('📋 Inicializando TODOS os funcionários da Encapsulação...');
      funcionarios.forEach(func => {
        if (func.setor === 'Encapsulação') {
          dadosPorFuncionario[func.nome] = criarEntradaFuncionario(func.nome, func.setor);
        }
      });
    } else {
      // Com funcionário selecionado: inicializar apenas esse funcionário
      console.log('📋 Inicializando apenas funcionário selecionado...');
      const funcionarioSelecionado = funcionarios.find(f => f.codigo === filtros.funcionario);
      if (funcionarioSelecionado) {
        dadosPorFuncionario[funcionarioSelecionado.nome] = criarEntradaFuncionario(
          funcionarioSelecionado.nome, funcionarioSelecionado.setor
        );
      }
    }
    
    console.log('📋 Funcionários inicializados:', Object.keys(dadosPorFuncionario).length);
    
    // Depois, processar os dados de produção
    dados.forEach(item => {
      const quantidade = item.quantidade || 0;
      const funcionarioNome = item.funcionario_nome || 'Sem nome';
      
      // Log específico para 30 cápsulas
      if (quantidade === 30) {
        console.log('🔍 Registro de 30 cápsulas encontrado:', item);
      }
      
      // Resumo geral por quantidade (todas as categorias, incluindo Outros)
      if (quantidade === 30) {
        resumoPorQuantidade[30]++;
      } else if (quantidade === 60) {
        resumoPorQuantidade[60]++;
      } else if (quantidade === 120) {
        resumoPorQuantidade[120]++;
      } else if (quantidade > 120) {
        resumoPorQuantidade['acima120']++;
      } else {
        resumoPorQuantidade['outros']++;  // Quantidades como 1, 5, 15 etc.
      }

      // Garantir que o funcionário existe no mapa
      if (!dadosPorFuncionario[funcionarioNome]) {
        console.log('➕ Adicionando funcionário dinamicamente:', funcionarioNome);
        dadosPorFuncionario[funcionarioNome] = criarEntradaFuncionario(
          funcionarioNome, item.funcionario_setor || 'Encapsulação'
        );
      }

      // Contar por faixas específicas — total só incrementa quando há categoria definida
      const entry = dadosPorFuncionario[funcionarioNome];
      if (quantidade === 30) {
        entry.quantidade30++;
        entry.total++;
      } else if (quantidade === 60) {
        entry.quantidade60++;
        entry.total++;
      } else if (quantidade === 120) {
        entry.quantidade120++;
        entry.total++;
      } else if (quantidade > 120) {
        entry.quantidadeAcima++;
        entry.total++;
      } else {
        entry.quantidadeOutros++;
        entry.total++;  // Contar tudo, incluindo outros
      }
    });
    
    // Converter para arrays ordenados
    const funcionariosOrdenados = Object.values(dadosPorFuncionario)
      .sort((a, b) => b.total - a.total); // Ordenar por total (maior para menor)
    
    console.log('📊 Resumo por quantidade:', resumoPorQuantidade);
    console.log('👥 Dados por funcionário:', funcionariosOrdenados.length, 'funcionários');
    console.log('🔍 Funcionários com produção:', funcionariosOrdenados.filter(f => f.total > 0).length);
    
    setResumoQuantidades(resumoPorQuantidade);
    setDadosFuncionarios(funcionariosOrdenados);
  }, [funcionarios, filtros]);

  // Carregar listas após inicialização
  useEffect(() => {
    carregarSetores();
    carregarFuncionarios();
  }, [carregarSetores, carregarFuncionarios]);

  // Carregar dados automaticamente quando mostrarDetalhes é alterado
  useEffect(() => {
    if (mostrarDetalhes && filtros.dataInicio && filtros.dataFim) {
      console.log('🚀 Auto-carregando dados para Encapsulação');
      console.log('⏰ Estado atual:', {
        mostrarDetalhes,
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim
      });
      
      const timer = setTimeout(() => {
        carregarDados();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [mostrarDetalhes, filtros.dataInicio, filtros.dataFim, carregarDados]);


  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ 
        p: 3, mb: 3, 
        background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)', 
        color: 'white', 
        borderRadius: 2 
      }}>
        <Box display="flex" alignItems="center">
          <TrendingUpIcon sx={{ mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Relatório de Volume de Produção
            </Typography>
            <Typography variant="subtitle1" opacity={0.9}>
              Análise completa do volume de produção por setor
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Início"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Funcionário (Opcional)</InputLabel>
              <Select
                value={filtros.funcionario}
                onChange={(e) => setFiltros(prev => ({ ...prev, funcionario: e.target.value }))}
                label="Funcionário (Opcional)"
              >
                <MenuItem value="">Todos os Funcionários</MenuItem>
                {funcionarios.map(funcionario => (
                  <MenuItem key={funcionario.codigo} value={funcionario.codigo}>
                    {funcionario.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>



          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={carregarDados}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <AssessmentIcon />}
              >
                {loading ? 'Carregando...' : 'Gerar Relatório'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={handlePrint}
                disabled={loading || dadosFuncionarios.length === 0}
                startIcon={<PrintIcon />}
                color="primary"
              >
                Imprimir
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleExportCSV}
                disabled={loading || dadosFuncionarios.length === 0}
                startIcon={<FileDownloadIcon />}
                color="success"
              >
                Exportar CSV
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleExportPDF}
                disabled={loading || dadosFuncionarios.length === 0}
                startIcon={<PictureAsPdfIcon />}
                color="error"
              >
                Exportar PDF
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Alerta de paginação — só exibe quando há mais registros além da página atual */}
      {temProximaPagina && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            📊 <strong>Mais dados disponíveis:</strong> Exibindo {dados.length} de {totalRegistros > 0 ? totalRegistros : 'muitos'} registros.
            Use a paginação para navegar pelos dados.
          </Typography>
        </Alert>
      )}

      {/* Resumo por Quantidade de Cápsulas */}
      {Object.keys(resumoQuantidades).length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Resumo Geral por Quantidade de Cápsulas
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3} md={3}>
              <Card sx={{ textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {resumoQuantidades[30] || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    30 cápsulas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <Card sx={{ textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" color="secondary.main" fontWeight="bold">
                    {resumoQuantidades[60] || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    60 cápsulas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <Card sx={{ textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {resumoQuantidades[120] || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    120 cápsulas
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Card sx={{ textAlign: 'center', height: '100%' }}>
                <CardContent>
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {resumoQuantidades['acima120'] || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Acima de 120
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {(resumoQuantidades['outros'] || 0) > 0 && (
              <Grid item xs={6} sm={2} md={2}>
                <Card sx={{ textAlign: 'center', height: '100%', borderLeft: '3px solid #9e9e9e' }}>
                  <CardContent>
                    <Typography variant="h4" color="text.secondary" fontWeight="bold">
                      {resumoQuantidades['outros']}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Outras qtd.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Estatísticas */}
      {estatisticas && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {mostrarDetalhes && filtros.setor ? (
            // Estatísticas para modo detalhado (produção de cápsulas)
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                      {estatisticas.total_leituras || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Requisições com Produção
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {estatisticas.total_capsulas || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {filtros.setor === 'Encapsulação' ? 'Total de Cápsulas' : 
                       filtros.setor === 'Ordem de Produção' ? 'Total de OPs' : 'Total Produzido'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {estatisticas.media_capsulas_por_requisicao || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {filtros.setor === 'Ordem de Produção' ? 'Média por OP' : 'Média por Requisição'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    {filtros.setor === 'Ordem de Produção' ? (
                      <FactoryIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    ) : (
                      <CapsuleIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      Setor: {filtros.setor}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Gráfico de Distribuição */}
              {distribuicao.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      📊 Distribuição por {filtros.setor === 'Ordem de Produção' ? 'Ordem de Produção' : 
                          filtros.setor === 'Encapsulação' ? 'Quantidade de Cápsulas' : 'Quantidade'}
                    </Typography>
                    
                    {/* Gráfico de Barras Simples */}
                    <Box sx={{ height: 300, mt: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                        {distribuicao.slice(0, 10).map((item, index) => (
                          <Box key={item.quantidade} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                              <Typography variant="body2" fontWeight="bold">
                                {item.quantidade}
                              </Typography>
                            </Box>
                            <Box sx={{ flex: 1, position: 'relative' }}>
                              <Box
                                sx={{
                                  height: 24,
                                  backgroundColor: '#e3f2fd',
                                  borderRadius: 1,
                                  position: 'relative',
                                  overflow: 'hidden'
                                }}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    backgroundColor: index < 3 ? '#1976d2' : '#42a5f5',
                                    width: `${Math.min((item.total_requisicoes / Math.max(...distribuicao.map(d => d.total_requisicoes))) * 100, 100)}%`,
                                    borderRadius: 1,
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                              </Box>
                            </Box>
                            <Box sx={{ minWidth: 80, textAlign: 'left' }}>
                              <Typography variant="body2" color="text.secondary">
                                {item.total_requisicoes} req.
                              </Typography>
                            </Box>
                            <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                {item.total_capsulas} caps.
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    
                    {/* Resumo das Top 5 quantidades */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Top 5 Quantidades Mais Produzidas:
                      </Typography>
                      <Grid container spacing={1}>
                        {distribuicao.slice(0, 5).map((item, index) => (
                          <Grid item xs={6} sm={4} md={2.4} key={item.quantidade}>
                            <Card variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                              <Typography variant="h6" color="primary.main" fontWeight="bold">
                                {item.quantidade}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.total_requisicoes} requisições
                              </Typography>
                              <Typography variant="caption" color="success.main" display="block">
                                {item.total_capsulas} cápsulas
                              </Typography>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </>
          ) : (
            // Estatísticas para modo resumido (funcionários)
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary.main" fontWeight="bold">
                      {estatisticas.total_funcionarios}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Funcionários Ativos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {estatisticas.total_requisicoes}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total de Requisições
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {estatisticas.media_requisicoes_por_funcionario}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Média por Funcionário
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <SpeedIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Performance Geral
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
        </Grid>
      )}

      {/* Tabela Detalhada por Funcionário */}
      {dadosFuncionarios.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold">
              📊 Produção por Funcionário
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Detalhamento por quantidade de cápsulas
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Funcionário</strong></TableCell>
                  <TableCell><strong>Setor</strong></TableCell>
                  <TableCell align="center"><strong>30 cápsulas</strong></TableCell>
                  <TableCell align="center"><strong>60 cápsulas</strong></TableCell>
                  <TableCell align="center"><strong>120 cápsulas</strong></TableCell>
                  <TableCell align="center"><strong>Acima de 120</strong></TableCell>
                  {dadosFuncionarios.some(f => (f.quantidadeOutros || 0) > 0) && (
                    <TableCell align="center"><strong>Outros</strong></TableCell>
                  )}
                  <TableCell align="center"><strong>Total</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dadosFuncionarios.map((funcionario, index) => (
                  <TableRow key={funcionario.nome} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {funcionario.nome}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {funcionario.setor || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={funcionario.quantidade30}
                        color={funcionario.quantidade30 > 0 ? "primary" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={funcionario.quantidade60}
                        color={funcionario.quantidade60 > 0 ? "secondary" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={funcionario.quantidade120}
                        color={funcionario.quantidade120 > 0 ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={funcionario.quantidadeAcima}
                        color={funcionario.quantidadeAcima > 0 ? "warning" : "default"}
                        size="small"
                      />
                    </TableCell>
                    {dadosFuncionarios.some(f => (f.quantidadeOutros || 0) > 0) && (
                      <TableCell align="center">
                        <Chip
                          label={funcionario.quantidadeOutros || 0}
                          color={(funcionario.quantidadeOutros || 0) > 0 ? "default" : "default"}
                          size="small"
                          sx={{ color: 'text.secondary' }}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {funcionario.total}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tabela Detalhada de Produção */}
      {mostrarDetalhes && dadosDetalhados.length > 0 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold">
              📋 Detalhes de Produção - {filtros.setor} ({dadosDetalhados.length} registros)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Período: {filtros.dataInicio} até {filtros.dataFim}
              {filtros.numeroOrdemProducao && (
                <> | Filtro: OP: {filtros.numeroOrdemProducao}</>
              )}
            </Typography>
          </Box>
          
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data/Hora</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Requisição</TableCell>
                  {filtros.setor === 'Ordem de Produção' && (
                    <TableCell sx={{ fontWeight: 'bold' }}>Nº OP</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 'bold' }}>Funcionário</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Setor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dadosDetalhados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                  <TableRow 
                    key={item.id} 
                    hover
                    sx={{
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {item.data_formatada}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.codigo_requisicao}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    {filtros.setor === 'Ordem de Produção' && (
                      <TableCell>
                        <Chip 
                          label={item.ordem_producao || item.codigo_requisicao || 'N/A'}
                          color="secondary"
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {item.funcionario_nome || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cód: {item.codigo_funcionario}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${item.quantidade} ${filtros.setor === 'Encapsulação' ? 'cáps' : 
                                 filtros.setor === 'Ordem de Produção' ? 'OPs' : 'und'}`}
                        color="success"
                        variant="filled"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.setor_efetivo}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalRegistros || dadosDetalhados.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 250]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>
      )}


      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      )}

      {/* Empty State */}
      {!loading && dados.length === 0 && !error && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <TrendingUpIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhum dado encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ajuste os filtros e tente novamente
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioVolumeProducao;